/**
 * 预制体序列化器
 * Prefab serializer
 *
 * 提供预制体的创建和实例化功能。
 * Provides prefab creation and instantiation functionality.
 */
import { EntitySerializer } from './EntitySerializer';
import { HierarchySystem } from '../Systems/HierarchySystem';
import { PrefabInstanceComponent } from '../Components/PrefabInstanceComponent';
/**
 * 预制体格式版本
 * Prefab format version
 */
export const PREFAB_FORMAT_VERSION = 1;
/**
 * 预制体序列化器类
 * Prefab serializer class
 *
 * 提供预制体的创建、序列化和实例化功能。
 * Provides prefab creation, serialization, and instantiation functionality.
 */
export class PrefabSerializer {
    /**
     * 从实体创建预制体数据
     * Create prefab data from entity
     *
     * @param entity - 源实体 | Source entity
     * @param options - 创建选项 | Creation options
     * @param hierarchySystem - 层级系统 | Hierarchy system
     * @returns 预制体数据 | Prefab data
     */
    static createPrefab(entity, options, hierarchySystem) {
        const includeChildren = options.includeChildren ?? true;
        // 序列化实体 | Serialize entity
        const serializedEntity = EntitySerializer.serialize(entity, includeChildren, hierarchySystem);
        // 转换为预制体实体格式 | Convert to prefab entity format
        const prefabEntity = this.toPrefabEntity(serializedEntity, true);
        // 收集组件类型信息 | Collect component type information
        const { componentTypes, componentTypeRegistry } = this.collectComponentTypes(prefabEntity);
        // 收集引用的资产（TODO: 实现资产引用扫描）
        // Collect referenced assets (TODO: implement asset reference scanning)
        const referencedAssets = [];
        const now = Date.now();
        const metadata = {
            name: options.name,
            createdAt: now,
            modifiedAt: now,
            componentTypes,
            referencedAssets
        };
        // 只在有值时添加可选属性 | Only add optional properties when they have values
        if (options.description) {
            metadata.description = options.description;
        }
        if (options.tags) {
            metadata.tags = options.tags;
        }
        return {
            version: PREFAB_FORMAT_VERSION,
            metadata,
            root: prefabEntity,
            componentTypeRegistry
        };
    }
    /**
     * 从预制体数据实例化实体
     * Instantiate entity from prefab data
     *
     * @param prefabData - 预制体数据 | Prefab data
     * @param scene - 目标场景 | Target scene
     * @param componentRegistry - 组件类型注册表 | Component type registry
     * @param options - 实例化选项 | Instantiation options
     * @returns 创建的根实体 | Created root entity
     */
    static instantiate(prefabData, scene, componentRegistry, options = {}) {
        const { parentId, name, preserveIds = false, trackInstance = true } = options;
        // 获取层级系统 | Get hierarchy system
        const hierarchySystem = scene.getSystem(HierarchySystem) ?? null;
        // ID 生成器 | ID generator
        let nextId = 1;
        const idGenerator = () => {
            while (scene.findEntityById(nextId)) {
                nextId++;
            }
            return nextId++;
        };
        // 反序列化实体 | Deserialize entity
        const { rootEntities, allEntities } = EntitySerializer.deserializeEntities([prefabData.root], componentRegistry, idGenerator, preserveIds, scene, hierarchySystem);
        const rootEntity = rootEntities[0];
        if (!rootEntity) {
            throw new Error('Failed to instantiate prefab: no root entity created');
        }
        // 覆盖名称 | Override name
        if (name) {
            rootEntity.name = name;
        }
        // 将所有实体添加到场景 | Add all entities to scene
        for (const entity of allEntities.values()) {
            scene.entities.add(entity);
        }
        // 设置父级 | Set parent
        if (parentId !== undefined && hierarchySystem) {
            const parent = scene.findEntityById(parentId);
            if (parent) {
                hierarchySystem.setParent(rootEntity, parent);
            }
        }
        // 添加预制体实例组件 | Add prefab instance component
        if (trackInstance) {
            const prefabGuid = prefabData.metadata.guid || '';
            this.addPrefabInstanceComponents(rootEntity, allEntities, prefabGuid, '', hierarchySystem);
        }
        // TODO: 应用位置、旋转、缩放覆盖（需要 TransformComponent）
        // TODO: Apply position, rotation, scale overrides (requires TransformComponent)
        return rootEntity;
    }
    /**
     * 将序列化实体转换为预制体实体格式
     * Convert serialized entity to prefab entity format
     */
    static toPrefabEntity(entity, isRoot) {
        const prefabEntity = {
            ...entity,
            isPrefabRoot: isRoot,
            children: entity.children.map(child => this.toPrefabEntity(child, false))
        };
        return prefabEntity;
    }
    /**
     * 收集预制体中使用的组件类型
     * Collect component types used in prefab
     */
    static collectComponentTypes(entity) {
        const typeMap = new Map();
        const collectFromEntity = (e) => {
            for (const comp of e.components) {
                if (!typeMap.has(comp.type)) {
                    typeMap.set(comp.type, comp.version);
                }
            }
            for (const child of e.children) {
                collectFromEntity(child);
            }
        };
        collectFromEntity(entity);
        const componentTypes = Array.from(typeMap.keys());
        const componentTypeRegistry = Array.from(typeMap.entries()).map(([typeName, version]) => ({ typeName, version }));
        return { componentTypes, componentTypeRegistry };
    }
    /**
     * 为实例化的实体添加预制体实例组件
     * Add prefab instance components to instantiated entities
     */
    static addPrefabInstanceComponents(rootEntity, allEntities, prefabGuid, prefabPath, _hierarchySystem) {
        const rootId = rootEntity.id;
        // 为根实体添加组件 | Add component to root entity
        const rootComp = new PrefabInstanceComponent(prefabGuid, prefabPath, true);
        rootComp.rootInstanceEntityId = rootId;
        rootEntity.addComponent(rootComp);
        // 为所有子实体添加组件 | Add component to all child entities
        for (const entity of allEntities.values()) {
            if (entity.id === rootId)
                continue;
            const childComp = new PrefabInstanceComponent(prefabGuid, prefabPath, false);
            childComp.rootInstanceEntityId = rootId;
            entity.addComponent(childComp);
        }
    }
    /**
     * 检查实体是否为预制体实例
     * Check if entity is a prefab instance
     */
    static isPrefabInstance(entity) {
        return entity.hasComponent(PrefabInstanceComponent);
    }
    /**
     * 获取预制体实例的源预制体 GUID
     * Get source prefab GUID of a prefab instance
     */
    static getSourcePrefabGuid(entity) {
        const comp = entity.getComponent(PrefabInstanceComponent);
        return comp?.sourcePrefabGuid || null;
    }
    /**
     * 获取预制体实例的根实体
     * Get root entity of a prefab instance
     */
    static getPrefabInstanceRoot(entity) {
        const comp = entity.getComponent(PrefabInstanceComponent);
        if (!comp || !comp.rootInstanceEntityId)
            return null;
        const scene = entity.scene;
        if (!scene)
            return null;
        return scene.findEntityById(comp.rootInstanceEntityId) || null;
    }
    /**
     * 将预制体数据序列化为 JSON 字符串
     * Serialize prefab data to JSON string
     */
    static serialize(prefabData, pretty = true) {
        return JSON.stringify(prefabData, null, pretty ? 2 : undefined);
    }
    /**
     * 从 JSON 字符串解析预制体数据
     * Parse prefab data from JSON string
     */
    static deserialize(json) {
        const data = JSON.parse(json);
        // 基本验证 | Basic validation
        if (!data.version || !data.metadata || !data.root) {
            throw new Error('Invalid prefab data format');
        }
        return data;
    }
    /**
     * 验证预制体数据格式
     * Validate prefab data format
     */
    static validate(prefabData) {
        const errors = [];
        if (typeof prefabData.version !== 'number') {
            errors.push('Invalid or missing version');
        }
        if (!prefabData.metadata) {
            errors.push('Missing metadata');
        }
        else {
            if (!prefabData.metadata.name) {
                errors.push('Missing metadata.name');
            }
            if (!Array.isArray(prefabData.metadata.componentTypes)) {
                errors.push('Invalid metadata.componentTypes');
            }
        }
        if (!prefabData.root) {
            errors.push('Missing root entity');
        }
        else {
            this.validateEntity(prefabData.root, errors, 'root');
        }
        if (!Array.isArray(prefabData.componentTypeRegistry)) {
            errors.push('Invalid componentTypeRegistry');
        }
        if (errors.length > 0) {
            return { valid: false, errors };
        }
        return { valid: true };
    }
    /**
     * 验证实体数据
     * Validate entity data
     */
    static validateEntity(entity, errors, path) {
        if (typeof entity.id !== 'number') {
            errors.push(`${path}: Invalid or missing id`);
        }
        if (typeof entity.name !== 'string') {
            errors.push(`${path}: Invalid or missing name`);
        }
        if (!Array.isArray(entity.components)) {
            errors.push(`${path}: Invalid or missing components`);
        }
        if (!Array.isArray(entity.children)) {
            errors.push(`${path}: Invalid or missing children`);
        }
        else {
            entity.children.forEach((child, index) => {
                this.validateEntity(child, errors, `${path}.children[${index}]`);
            });
        }
    }
}
//# sourceMappingURL=PrefabSerializer.js.map